/**
 * Regel "Volgende stap" direct boven de knoppen van de actiebalk (besluit Asil, 2 oktober 2026).
 *
 * Eén regel, altijd zichtbaar in een workflow-pane: "Volgende stap: 2 · Opzet — vul de materialiteit in"
 * zonder zichtbare link (sinds 16 oktober 2026 geïntegreerd in de balk); een klik (of Enter) opent dat menu. Klaar = gedempt groen
 * ("Klaar — dossier geschreven om 14:32"), klikbaar naar de laatste stap of de optionele suggestie;
 * tijdens een handeling "Bezig: …" (niet klikbaar). De tekst komt uit pickNextStep met de statusteksten
 * van de menu's; deze component kiest niets zelf.
 *
 * Gebruik: <ActionBar next={<NextStepLine next={next} onOpen={id => setOpenMenu(id)} />} …>
 */

import * as React from "react";
import { compactEurosInText } from "./compactAmount";
import { NextStep, nextStepSeparator, nextStepTarget, nextStepText } from "./nextStep";
import { shellText } from "./shellText";
import { useLang } from "../../i18n/LangContext";
import "./pane.css";

export interface NextStepLineProps<Id extends string = string> {
  next: NextStep<Id> | null;
  /** Opent het menu van de stap. */
  onOpen: (id: Id) => void;
  /**
   * Wat een klik doet, als dat niet het menu openen is (ToD/SmartTie met open regels: naar de eerste open
   * regel; besluit Asil 4 oktober 2026). Alleen tooltip en toegankelijke naam; de zichtbare tekst blijft.
   */
  actionLabel?: string;
}

export function NextStepLine<Id extends string>({
  next,
  onOpen,
  actionLabel,
}: NextStepLineProps<Id>): JSX.Element | null {
  const lang = useLang();
  if (!next) return null;
  const full = nextStepText(next);
  const target = nextStepTarget(next);
  const detail = compactEurosInText(next.detail);
  const sep = nextStepSeparator(next);
  const content = (
    <>
      <span className="pane-nextstep__text">
        <strong className="pane-nextstep__head">{next.head}</strong>
        {sep}
        {detail}
      </span>
    </>
  );
  if (!target) {
    return (
      <div className={`pane-nextstep is-${next.kind}`} role="status" title={full}>
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`pane-nextstep is-${next.kind}`}
      title={`${full} (${actionLabel ? `${shellText("clickPrefix", lang)}: ${actionLabel.charAt(0).toLowerCase()}${actionLabel.slice(1)}` : shellText("clickOpenMenu", lang)})`}
      aria-label={`${full}. ${actionLabel ?? shellText("openMenu", lang)}.`}
      onClick={() => onOpen(target)}
    >
      {content}
    </button>
  );
}
